import { useState, useCallback } from 'react'
import { Plus, RefreshCw, History, Settings, ArrowLeft } from 'lucide-react'
import { Link } from 'react-router-dom'
import { useSyncSchedules, useSyncHistory, useCollections } from '../components/knowledge/hooks'
import { SyncScheduleList, SyncScheduleForm, SyncHistoryTable } from '../components/knowledge/sync'
import type { SyncSchedule, SyncScheduleCreate, SyncScheduleUpdate } from '../components/knowledge/types'

/**
 * Page de gestion des synchronisations WordPress
 * Layout Master-Detail avec liste des configs à gauche et historique à droite
 */
export default function SyncPage() {
  // State
  const [showForm, setShowForm] = useState(false)
  const [editingSchedule, setEditingSchedule] = useState<SyncSchedule | null>(null)
  const [selectedSchedule, setSelectedSchedule] = useState<SyncSchedule | null>(null)
  const [deleteConfirm, setDeleteConfirm] = useState<SyncSchedule | null>(null)

  // Hooks
  const {
    schedules,
    loading: schedulesLoading,
    error: schedulesError,
    reload: reloadSchedules,
    create: createSchedule,
    update: updateSchedule,
    remove: removeSchedule,
    toggle: toggleSchedule,
    trigger: triggerSchedule,
  } = useSyncSchedules()

  const {
    history,
    loading: historyLoading,
    hasMore,
    total: historyTotal,
    reload: reloadHistory,
    loadMore: loadMoreHistory,
    loadScheduleHistory,
  } = useSyncHistory()

  const { collections } = useCollections()

  // Handlers
  const handleCreate = useCallback(() => {
    setEditingSchedule(null)
    setShowForm(true)
  }, [])

  const handleEdit = useCallback((schedule: SyncSchedule) => {
    setEditingSchedule(schedule)
    setShowForm(true)
  }, [])

  const handleDelete = useCallback((schedule: SyncSchedule) => {
    setDeleteConfirm(schedule)
  }, [])

  const confirmDelete = useCallback(async () => {
    if (!deleteConfirm) return
    await removeSchedule(deleteConfirm.public_id)
    setDeleteConfirm(null)
    if (selectedSchedule?.public_id === deleteConfirm.public_id) {
      setSelectedSchedule(null)
    }
  }, [deleteConfirm, removeSchedule, selectedSchedule])

  const handleToggle = useCallback(async (schedule: SyncSchedule) => {
    await toggleSchedule(schedule.public_id)
  }, [toggleSchedule])

  const handleTrigger = useCallback(async (schedule: SyncSchedule) => {
    const result = await triggerSchedule(schedule.public_id)
    if (result) {
      // Refresh history after a short delay
      setTimeout(() => {
        if (selectedSchedule) {
          loadScheduleHistory(selectedSchedule.public_id)
        } else {
          reloadHistory()
        }
      }, 1000)
    }
  }, [triggerSchedule, selectedSchedule, loadScheduleHistory, reloadHistory])

  const handleSelect = useCallback((schedule: SyncSchedule) => {
    setSelectedSchedule(schedule)
    loadScheduleHistory(schedule.public_id)
  }, [loadScheduleHistory])

  const handleShowAllHistory = useCallback(() => {
    setSelectedSchedule(null)
    reloadHistory()
  }, [reloadHistory])

  const handleSubmit = useCallback(async (data: SyncScheduleCreate): Promise<boolean> => {
    if (editingSchedule) {
      const updateData: SyncScheduleUpdate = {
        name: data.name,
        target_collection_id: data.target_collection_id,
        frequency: data.frequency,
        preferred_hour: data.preferred_hour,
        preferred_day: data.preferred_day,
        enabled: data.enabled,
      }
      return await updateSchedule(editingSchedule.public_id, updateData)
    } else {
      const result = await createSchedule(data)
      return result !== null
    }
  }, [editingSchedule, updateSchedule, createSchedule])

  const loading = schedulesLoading
  const error = schedulesError

  return (
    <div className="h-full flex flex-col bg-zinc-50 dark:bg-zinc-900">
      {/* Header */}
      <header className="flex-shrink-0 bg-white dark:bg-zinc-800 border-b border-zinc-200 dark:border-zinc-700 px-6 py-4">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Link
              to="/knowledges"
              className="p-2 rounded-lg hover:bg-zinc-100 dark:hover:bg-zinc-700 transition-colors"
            >
              <ArrowLeft className="w-5 h-5 text-zinc-500" />
            </Link>
            <div>
              <h1 className="text-xl font-semibold text-zinc-900 dark:text-zinc-100">
                Synchronisations WordPress
              </h1>
              <p className="text-sm text-zinc-500 dark:text-zinc-400">
                Configurez vos imports automatiques depuis WordPress
              </p>
            </div>
          </div>
          
          <div className="flex items-center gap-2">
            <button
              onClick={() => { reloadSchedules(); reloadHistory() }}
              disabled={loading}
              className="inline-flex items-center gap-2 px-3 py-2 text-sm font-medium rounded-lg
                text-zinc-700 dark:text-zinc-300 hover:bg-zinc-100 dark:hover:bg-zinc-700
                disabled:opacity-50 transition-colors"
            >
              <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
              Actualiser
            </button>
            
            <button
              onClick={handleCreate}
              className="inline-flex items-center gap-2 px-4 py-2 text-sm font-medium rounded-lg
                bg-blue-600 text-white hover:bg-blue-700 transition-colors"
            >
              <Plus className="w-4 h-4" />
              Nouvelle sync
            </button>
          </div>
        </div>
      </header>

      {/* Error */}
      {error && (
        <div className="mx-6 mt-4 p-4 rounded-lg bg-red-50 dark:bg-red-900/20 text-red-700 dark:text-red-300">
          {error}
        </div>
      )}

      {/* Main content - Master/Detail layout */}
      <div className="flex-1 flex min-h-0 overflow-hidden">
        {/* Left panel - Schedules list */}
        <div className="w-1/2 border-r border-zinc-200 dark:border-zinc-700 flex flex-col">
          <div className="flex-shrink-0 px-4 py-3 bg-zinc-100/50 dark:bg-zinc-800/50 border-b border-zinc-200 dark:border-zinc-700">
            <div className="flex items-center gap-2">
              <Settings className="w-4 h-4 text-zinc-500" />
              <h2 className="text-sm font-medium text-zinc-700 dark:text-zinc-300">
                Configurations ({schedules.length})
              </h2>
            </div>
          </div>
          
          <div className="flex-1 overflow-y-auto p-4">
            <SyncScheduleList
              schedules={schedules}
              loading={schedulesLoading}
              onEdit={handleEdit}
              onDelete={handleDelete}
              onToggle={handleToggle}
              onTrigger={handleTrigger}
              selectedId={selectedSchedule?.public_id}
              onSelect={handleSelect}
            />
          </div>
        </div>

        {/* Right panel - History */}
        <div className="w-1/2 flex flex-col">
          <div className="flex-shrink-0 px-4 py-3 bg-zinc-100/50 dark:bg-zinc-800/50 border-b border-zinc-200 dark:border-zinc-700">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <History className="w-4 h-4 text-zinc-500" />
                <h2 className="text-sm font-medium text-zinc-700 dark:text-zinc-300">
                  {selectedSchedule 
                    ? `Historique: ${selectedSchedule.name}` 
                    : `Historique global (${historyTotal})`}
                </h2>
              </div>
              
              {selectedSchedule && (
                <button
                  onClick={handleShowAllHistory}
                  className="text-xs text-blue-600 hover:text-blue-700 dark:text-blue-400"
                >
                  Voir tout l'historique
                </button>
              )}
            </div>
          </div>
          
          <div className="flex-1 overflow-y-auto p-4">
            <SyncHistoryTable
              history={history}
              loading={historyLoading}
              hasMore={hasMore}
              total={historyTotal}
              onLoadMore={loadMoreHistory}
              showScheduleName={!selectedSchedule}
            />
          </div>
        </div>
      </div>

      {/* Create/Edit Form Modal */}
      {showForm && (
        <SyncScheduleForm
          schedule={editingSchedule}
          collections={collections}
          onSubmit={handleSubmit}
          onCancel={() => { setShowForm(false); setEditingSchedule(null) }}
        />
      )}

      {/* Delete Confirmation Modal */}
      {deleteConfirm && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
          <div className="bg-white dark:bg-zinc-800 rounded-xl shadow-xl w-full max-w-md p-6">
            <h3 className="text-lg font-semibold text-zinc-900 dark:text-zinc-100 mb-2">
              Supprimer la configuration ?
            </h3>
            <p className="text-sm text-zinc-600 dark:text-zinc-400 mb-4">
              La configuration <strong>"{deleteConfirm.name}"</strong> et tout son historique seront supprimés définitivement.
            </p>
            <div className="flex justify-end gap-3">
              <button
                onClick={() => setDeleteConfirm(null)}
                className="px-4 py-2 text-sm font-medium rounded-lg
                  text-zinc-700 dark:text-zinc-300 hover:bg-zinc-100 dark:hover:bg-zinc-700"
              >
                Annuler
              </button>
              <button
                onClick={confirmDelete}
                className="px-4 py-2 text-sm font-medium rounded-lg
                  bg-red-600 text-white hover:bg-red-700"
              >
                Supprimer
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}
